import { trackClickEvent } from "../../main/events/clicks-tracker";
import {
  trackFunnelClick,
  trackFunnelView,
} from "../../main/events/funnel-tracker";
import { ga4Event } from "../../main/utils/analytics";
import type { EVENTS } from "../../main/events/event-list";
import {
  PAYWALL_EXPERIMENT_ID,
  paywallVariant,
  trackPaywallExperiment,
} from "./experiment";

/**
 * CTA tracking, for Lume.
 *
 * THE ATTRIBUTES DO NOT WORK IN HERE, and that is the whole reason this file
 * exists. The site tracks its CTAs declaratively — `data-funnel-click-*` on the
 * link, plus `data-track-click` — and both are picked up by delegated listeners
 * on `document`, with an IntersectionObserver over `document.body` for the view
 * events. Lume renders inside a SHADOW ROOT, where none of the three reach:
 *
 *   clicks   retarget to the host element on the way out of the shadow tree, so
 *            `event.target.closest('[data-funnel-click-element]')` sees the host
 *            and never the CTA.
 *   views    MutationObserver and querySelectorAll do not descend into a shadow
 *            root, so the observer never learns the CTA exists.
 *
 * So Lume's CTAs report themselves, through the same functions the delegated
 * listeners call — same GA4 events, same last-touch attribution (which the
 * server-side purchase event reads), same event-type ids. Putting the attributes
 * on the element as well would be dead weight that reads as if it worked.
 */

/** The three funnel dimensions, as GA4 receives them. */
export interface FunnelCta {
  location: string;
  element: string;
  type: string;
}

/**
 * The arm, on every funnel event Lume sends.
 *
 * Sent per event rather than configured once as a GA4 user property, because
 * `experiment_variant` has to be readable on the event itself for a CTR cut —
 * clicks ÷ views within one arm — and a user-scoped dimension would only tell
 * you which arm the session was in, not which arm each impression belonged to.
 */
function experimentDims() {
  return {
    experiment_id: PAYWALL_EXPERIMENT_ID,
    experiment_variant: paywallVariant(),
  };
}

/**
 * Announces a CTA that has just appeared on screen.
 *
 * Deduped by (location, element, type) inside the tracker, so a modal reopened
 * three times still counts one view — the same rule the observer applies to a
 * CTA scrolled past twice. The variant is NOT part of that key: see
 * FunnelExtras in funnel-tracker.
 *
 * Also the point at which the experiment itself is reported. A banner appearing
 * is the only proof the student was ever exposed to the test — assignment
 * happens on every Lume page load, and most of those never show a paywall.
 */
export function trackCtaView(cta: FunnelCta): void {
  trackFunnelView(cta.location, cta.element, cta.type, experimentDims());
  trackPaywallExperiment();
}

/**
 * Announces a CTA click: the funnel event and — when the CTA has an event id of
 * its own — the site's own click event.
 *
 * Call it from the handler and let the link navigate; both trackers are
 * fire-and-forget (dataLayer push, sendBeacon), so neither delays the
 * navigation and neither needs the click to be cancelled.
 */
export function trackCtaClick(
  cta: FunnelCta,
  event?: keyof typeof EVENTS
): void {
  trackFunnelClick(cta.location, cta.element, cta.type, experimentDims());

  if (event) {
    trackClickEvent(event);
  }
}

/**
 * The funnel events for a CTA that is not a paywall — the Tutor's starter
 * cards. No experiment dims and no exposure report: neither test is on screen.
 * The click skips saveLastCta too, so a starter card never takes a paywall's
 * purchase attribution or checkout return.
 */
export function trackPlainCtaView(cta: FunnelCta): void {
  trackFunnelView(cta.location, cta.element, cta.type);
}

export function trackPlainCtaClick(cta: FunnelCta): void {
  trackFunnelClick(cta.location, cta.element, cta.type, undefined, { attribute: false });
}

/**
 * The same two events for the conversion modal. Same test as the banners: its
 * title is one of the slots the arm decides.
 *
 * ── THE STEP THIS ADDS TO THE FUNNEL ────────────────────────────────────────
 * A banner's funnel_cta_click no longer means "went to the checkout"; it means
 * "opened the modal". These two are what restore the second half: the modal
 * announces itself on open and reports the press that actually leaves for the
 * checkout. So there ARE two clicks per conversion now, under two different
 * `element` values — the banner's own, and `conversion_modal`. Any report that
 * sums clicks across elements will double-count from the cutover; the fix is to
 * read the two steps as a funnel, which is what the element split is for.
 */
export function trackModalView(cta: FunnelCta): void {
  trackFunnelView(cta.location, cta.element, cta.type, experimentDims());
  trackPaywallExperiment();
}

export function trackModalClick(
  cta: FunnelCta,
  event?: keyof typeof EVENTS
): void {
  trackFunnelClick(cta.location, cta.element, cta.type, experimentDims());

  if (event) {
    trackClickEvent(event);
  }
}

/** The chat unlock widget's view. Its click is an ordinary trackCtaClick. */
const UNLOCK_VIEWS_KEY = "lume_unlock_views";
const UNLOCK_VIEWS_KEPT = 200;
const seenUnlockViews = new Set<string>();

function storedUnlockViews(): string[] {
  try {
    const stored: unknown = JSON.parse(localStorage.getItem(UNLOCK_VIEWS_KEY) ?? "[]");

    return Array.isArray(stored) ? stored.filter((id) => typeof id === "string") : [];
  } catch {
    return [];
  }
}

/**
 * One view per card, ever: keyed by the card's tool call id and remembered
 * across reloads, so reopening a thread does not count old cards again. Not
 * trackFunnelView, whose dedupe is per (location, element, type) and would let
 * only the first card of a page load report.
 */
export function trackUnlockView(cta: FunnelCta, widgetId: string): void {
  if (seenUnlockViews.has(widgetId)) return;
  seenUnlockViews.add(widgetId);

  const stored = storedUnlockViews();
  if (stored.includes(widgetId)) return;

  try {
    localStorage.setItem(
      UNLOCK_VIEWS_KEY,
      JSON.stringify([...stored, widgetId].slice(-UNLOCK_VIEWS_KEPT))
    );
  } catch {
    // Storage blocked: the in-memory set still dedupes this page load.
  }

  ga4Event({
    event: "funnel_cta_view",
    location: cta.location,
    element: cta.element,
    type: cta.type,
    ...experimentDims(),
  });
  trackPaywallExperiment();
}
